import * as React from 'react'
import produce from 'immer'
import { Form, Input, Spin, Tabs, Button, Popover } from 'antd'
import { get, isEmpty, cloneDeep, set } from 'lodash'
import { showError, resourceNotFound, ValidationError } from 'helpers/errors'
import { moveByDelta, HARD_SPACE } from 'helpers/utils'
import { createLabelFactory, createFieldFactory, createHandleSubmit, isReadOnly } from 'helpers/formViews'
import { formatUserTime } from 'helpers/dateTime'
import { getUserTagSettings, getUserTagSettingItems } from 'helpers/procurement'
import approverSelectionTypes from 'options/users/approverSelectionTypes'
import reqApprovalTypes from 'options/users/reqApprovalTypes'
import orderApprovalTypes from 'options/users/orderApprovalTypes'
import { t } from 'helpers/i18n'
import { stopEvent } from 'helpers/events'
import FormButtons from 'elements/FormButtons'
import Checkbox from 'elements/Checkbox'
import Icon from 'elements/Icon'
import Divider from 'elements/Divider'
import CheckboxList from 'elements/CheckboxList'
import InputNumber from 'elements/InputNumber'
import Tags from 'containers/Users/FormView/Tags'
import Select, { Option } from 'elements/Select'
import Permissions from 'containers/Profiles/FormView/Permissions'
import { Row, Col } from 'elements/Grid'

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
  }

  componentDidMount() {
    this.fetchItem()
  }

  saveItemSuccess = () => this.props.refreshToken(true)

  getItemId = () => [this.state.item?.id, this.props.linkTargetRecord?.profileId].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({})
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType }),
      ])

      const item = cloneDeep(responses[0].value.data)

      const [locations, suppliers, userGroups, users] = await Promise.all([
        this.props.getLocations({ includeAllLocations: true }),
        this.props.getSuppliers(),
        this.props.getUserGroups(),
        this.props.getUsers(),
      ])

      this.setState({
        item,
        fieldSettings: get(responses[1], 'value.data.fieldSettings', []),
        locations: get(locations, 'value.data.items', []),
        suppliers: get(suppliers, 'value.data.items', []),
        userGroups: get(userGroups, 'value.data.items', []),
        users: get(users, 'value.data.items', [])
          .slice()
          .sort((a, b) => {
            const m = item.coreUserSettings.approvers.indexOf(a.userName)
            const n = item.coreUserSettings.approvers.indexOf(b.userName)
            return m === -1 && n !== -1 ? 1 : m !== -1 && n === -1 ? -1 : m - n
          }),
      })
    } catch (error) {
      showError({ error })
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      })
    )

  handleTagsChange = (values) =>
    this.setState(
      produce((draft) => {
        draft.item.coreUserSettings.tagSettings = getUserTagSettings(values)
      })
    )

  handlePermissionsChange = (values) =>
    this.setState(
      produce((draft) => {
        draft.item.coreUserSettings.permissions = values
      })
    )

  moveHighlightedApprover = (delta) =>
    this.setState(
      produce((draft) => {
        draft.users = moveByDelta(
          draft.users,
          draft.users.findIndex((one) => one.userName === draft.highlightedApprover),
          delta
        )
        draft.item.coreUserSettings.approvers = moveByDelta(
          draft.item.coreUserSettings.approvers,
          draft.item.coreUserSettings.approvers.findIndex((one) => one === draft.highlightedApprover),
          delta
        )
      })
    )

  render() {
    const { form, customer, tenantGroupIsCardinal } = this.props
    const { item, fieldSettings, locations, userGroups, suppliers, users, highlightedApprover } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)
    const readOnly = isReadOnly(this)

    const userDefinedApproverSelection = item.coreUserSettings.approverSelection === 'UserDefinedList'

    return (
      <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
        <div className="form-items-container">
          <ValidationError errors={this.state.validationErrors} />
          <Row>
            <Col third>
              <Form.Item label={createLabel('name')}>
                {createFieldDecorator('name')(<Input readOnly={readOnly} autoFocus />)}
              </Form.Item>
              <Form.Item label={createLabel('description')}>
                {createFieldDecorator('description')(<Input readOnly={readOnly} />)}
              </Form.Item>
            </Col>
            <Col third>
              <Form.Item label={t('created')}>
                <Input value={formatUserTime(item.createDate, item.createdBy) || t('na')} disabled />
              </Form.Item>
              <Form.Item label={t('userCount')}>
                <Row>
                  <Col xs={12}>
                    <Input value={item.userCount} disabled />
                  </Col>
                  <Col xs={12}>
                    <Popover
                      title={t('users')}
                      content={<span dangerouslySetInnerHTML={{ __html: item.userCountInfo }} />}
                    >
                      <span
                        className="align-middle"
                        style={{ visibility: isEmpty(item.userCountInfo) ? 'hidden' : 'visible' }}
                      >
                        <Icon type="Search" />
                      </span>
                    </Popover>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
            <Col third>
              <Form.Item label={HARD_SPACE}>
                {createFieldDecorator('active', { valuePropName: 'checked' })(
                  <Checkbox readOnly={readOnly}>{createLabel('active')}</Checkbox>
                )}
              </Form.Item>
            </Col>
          </Row>
          <Tabs>
            <Tabs.TabPane key="permissions" tab={t('permissions')} forceRender>
              <Form.Item style={{ marginBottom: '-12px' }}>
                {createFieldDecorator('includeAuthorizations', { valuePropName: 'checked' })(
                  <Checkbox readOnly={readOnly}>{createLabel('includeAuthorizations')}</Checkbox>
                )}
              </Form.Item>
              <Divider />
              <Permissions
                values={item.coreUserSettings.permissions}
                onChange={this.handlePermissionsChange}
                readOnly={readOnly}
                disabled={form.getFieldValue('includeAuthorizations') !== true}
              />
            </Tabs.TabPane>
            <Tabs.TabPane key="locations" tab={t('locations')} forceRender>
              <Form.Item style={{ marginBottom: '-12px' }}>
                {createFieldDecorator('includeLocations', { valuePropName: 'checked' })(
                  <Checkbox readOnly={readOnly}>{createLabel('includeLocations')}</Checkbox>
                )}
              </Form.Item>
              <Divider />
              <Row>
                <Col md={12} xs={24}>
                  <Form.Item label={createLabel('coreUserSettings.locationIds')}>
                    <CheckboxList
                      dataSource={locations}
                      value={
                        JSON.stringify(item.coreUserSettings.locationIds) !== '[0]'
                          ? item.coreUserSettings.locationIds
                          : locations.map((each) => each.id)
                      }
                      onChange={(values) =>
                        this.setItemValue(
                          'coreUserSettings.locationIds',
                          values.length === locations.length ? [0] : values
                        )
                      }
                      readOnly={readOnly}
                      disabled={form.getFieldValue('includeLocations') !== true}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Tabs.TabPane>
            <Tabs.TabPane key="groups" tab={t('groups')} forceRender>
              <Form.Item style={{ marginBottom: '-12px' }}>
                {createFieldDecorator('includeUserGroups', { valuePropName: 'checked' })(
                  <Checkbox readOnly={readOnly}>{createLabel('includeUserGroups')}</Checkbox>
                )}
              </Form.Item>
              <Divider />
              <Row>
                <Col md={12} xs={24}>
                  <Form.Item label={createLabel('coreUserSettings.userGroupIds')}>
                    <CheckboxList
                      dataSource={userGroups}
                      value={item.coreUserSettings.userGroupIds}
                      onChange={(values) => this.setItemValue('coreUserSettings.userGroupIds', values)}
                      readOnly={readOnly}
                      disabled={form.getFieldValue('includeUserGroups') !== true}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Tabs.TabPane>
            <Tabs.TabPane key="approvals" tab={t('approvals')} forceRender>
              <Form.Item style={{ marginBottom: '-12px' }}>
                {createFieldDecorator('includeApprovals', { valuePropName: 'checked' })(
                  <Checkbox readOnly={readOnly}>{createLabel('includeApprovals')}</Checkbox>
                )}
              </Form.Item>
              <Divider />
              <Row>
                <Col md={10} xs={24}>
                  <Form.Item label={createLabel('coreUserSettings.approverSelection')}>
                    {createFieldDecorator('coreUserSettings.approverSelection')(
                      <Select
                        onChange={(value) => this.setItemValue('coreUserSettings.approverSelection', value)}
                        readOnly={readOnly}
                        allowClear={false}
                        disabled={form.getFieldValue('includeApprovals') !== true}
                      >
                        {Object.entries(approverSelectionTypes).map(([key, value]) => (
                          <Option key={key} value={key}>
                            {t(value)}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
              </Row>
              <Divider className="my-12" />
              <Row>
                <Col md={12} xs={24}>
                  <Row type="flex" justify="space-between" align="middle">
                    <Col xs={20}>
                      <Form.Item label={createLabel('coreUserSettings.approvers')}>
                        <CheckboxList
                          idField="userName"
                          dataSource={users}
                          value={item.coreUserSettings.approvers}
                          onChange={(values) => this.setItemValue('coreUserSettings.approvers', values)}
                          readOnly={readOnly}
                          onHighlight={(value) => this.setState({ highlightedApprover: value })}
                          highlighted={highlightedApprover}
                          disabled={
                            form.getFieldValue('includeApprovals') !== true || !userDefinedApproverSelection
                          }
                          itemsStyle={{ maxHeight: '240px' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={4}>
                      <Button
                        className="mb-12"
                        onClick={() => this.moveHighlightedApprover(-1)}
                        disabled={!highlightedApprover}
                      >
                        <Icon type="KeyboardArrowUp" />
                      </Button>
                      <Button onClick={() => this.moveHighlightedApprover(1)} disabled={!highlightedApprover}>
                        <Icon type="KeyboardArrowDown" />
                      </Button>
                    </Col>
                  </Row>
                </Col>
                <Col md={12} xs={24}>
                  <Form.Item label={createLabel('coreUserSettings.reqApproval')}>
                    {createFieldDecorator('coreUserSettings.reqApproval')(
                      <Select
                        readOnly={readOnly}
                        allowClear={false}
                        disabled={
                          form.getFieldValue('includeApprovals') !== true || !userDefinedApproverSelection
                        }
                      >
                        {Object.entries(reqApprovalTypes).map(([key, value]) => (
                          <Option key={key} value={key}>
                            {t(value)}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                  <Form.Item label={createLabel('coreUserSettings.orderApproval')}>
                    {createFieldDecorator('coreUserSettings.orderApproval')(
                      <Select
                        readOnly={readOnly}
                        allowClear={false}
                        disabled={
                          form.getFieldValue('includeApprovals') !== true || !userDefinedApproverSelection
                        }
                      >
                        {Object.entries(orderApprovalTypes).map(([key, value]) => (
                          <Option key={key} value={key}>
                            {t(value)}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Tabs.TabPane>
            <Tabs.TabPane key="tags" tab={t('tags')} forceRender>
              <Form.Item style={{ marginBottom: '-12px' }}>
                {createFieldDecorator('includeDataEntry', { valuePropName: 'checked' })(
                  <Checkbox readOnly={readOnly}>{createLabel('includeDataEntry')}</Checkbox>
                )}
              </Form.Item>
              <Divider />
              {createFieldDecorator('coreUserSettings.overrideTags', { valuePropName: 'checked' })(
                <Checkbox readOnly={readOnly} disabled={form.getFieldValue('includeDataEntry') !== true}>
                  {createLabel('coreUserSettings.overrideTags')}
                </Checkbox>
              )}
              <Tags
                items={getUserTagSettingItems(item.coreUserSettings.tagSettings)}
                onChange={this.handleTagsChange}
                readOnly={readOnly}
                disabled={
                  form.getFieldValue('includeDataEntry') !== true ||
                  form.getFieldValue('coreUserSettings.overrideTags') !== true
                }
              />
            </Tabs.TabPane>
            {tenantGroupIsCardinal && (
              <Tabs.TabPane key="suppliers" tab={t('suppliers')} forceRender>
                <Form.Item style={{ marginBottom: '-12px' }}>
                  {createFieldDecorator('includeSuppliers', { valuePropName: 'checked' })(
                    <Checkbox readOnly={readOnly}>{createLabel('includeSuppliers')}</Checkbox>
                  )}
                </Form.Item>
                <Divider />
                <Row>
                  <Col md={12} xs={24}>
                    <Form.Item label={createLabel('coreUserSettings.supplierIds')}>
                      <CheckboxList
                        dataSource={suppliers}
                        value={item.coreUserSettings.supplierIds}
                        onChange={(values) => this.setItemValue('coreUserSettings.supplierIds', values)}
                        readOnly={readOnly}
                        disabled={form.getFieldValue('includeSuppliers') !== true}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Tabs.TabPane>
            )}
            {customer.passwordSettings.definitionMode === 'Advanced' && (
              <Tabs.TabPane key="password" tab={t('password')} forceRender>
                <Form.Item style={{ marginBottom: '-12px' }}>
                  {createFieldDecorator('includePassword', { valuePropName: 'checked' })(
                    <Checkbox readOnly={readOnly}>{createLabel('includePassword')}</Checkbox>
                  )}
                </Form.Item>
                <Divider />
                <Row>
                  <Col third>
                    <Form.Item label={createLabel('coreUserSettings.passwordExpirationDays')}>
                      {createFieldDecorator('coreUserSettings.passwordExpirationDays')(
                        <InputNumber
                          readOnly={readOnly}
                          disabled={form.getFieldValue('includePassword') !== true}
                          min={0}
                        />
                      )}
                    </Form.Item>
                    {createFieldDecorator('coreUserSettings.passwordMustChangeOnNextLogin', {
                      valuePropName: 'checked',
                    })(
                      <Checkbox readOnly={readOnly} disabled={form.getFieldValue('includePassword') !== true}>
                        {createLabel('coreUserSettings.passwordMustChangeOnNextLogin')}
                      </Checkbox>
                    )}
                  </Col>
                </Row>
              </Tabs.TabPane>
            )}
          </Tabs>
        </div>
        <div className="form-buttons-container">
          <FormButtons
            readOnly={readOnly}
            onCancel={this.props.onCancel}
            onSubmit={this.handleSubmit}
            saveButtonLoading={this.state.saveButtonLoading}
            saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
          />
        </div>
      </Form>
    )
  }
}

export default Form.create()(FormView)
